import {t} from "@lingui/core/macro";
import * as React from "react";
import {useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {Spring, Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelSmall} from "@khanacademy/wonder-blocks-typography";
import {redirect} from "@khan/urls";

import JoinStudentMutation from "./join-student-mutation.tsx";

type Props = {
    // The class code of the class we're about to join
    classCode: string;
    // The name of the class we're about to join
    className: string;
    // Called when the user wants to join a different class
    onJoinDifferentClass: () => unknown;
};

/**
 * LoggedInJoinPrompt asks the user whether they'd like to actually join the
 * class or not.
 *
 * If yes, we join immediately via a mutation, and redirect when done.
 *
 * If not, we navigate back to the class code input.
 */
export default function LoggedInJoinPrompt({
    classCode,
    className,
    onJoinDifferentClass,
}: Props): React.ReactElement {
    const [redirecting, setRedirecting] = useState(false);

    const handleCompleted = (errors?: string | null) => {
        if (errors != null) {
            // Joining failed! Don't redirect, and let the Mutation display
            // the error!
            return;
        }

        // Joining succeeded! Redirect to the homepage. (The spinner will
        // continue to show!)
        setRedirecting(true);
        redirect(`/profile/me/class/${encodeURIComponent(classCode)}`);
    };

    return (
        <JoinStudentMutation
            classCode={classCode}
            onCompleted={handleCompleted}
        >
            {(mutate, {loading, error, data}) => {
                const err = data?.joinStudent.error;
                const errorMessage = data?.joinStudent.errors || error?.message;
                const showSpinner = loading || redirecting;

                return (
                    <>
                        <Button
                            kind="primary"
                            onClick={() => mutate()}
                            spinner={showSpinner}
                            testId="confirm-join-class-button"
                        >
                            {t`Join ${className}`}
                        </Button>
                        <Strut size={spacing.xSmall_8} />
                        <Button
                            kind="secondary"
                            onClick={onJoinDifferentClass}
                            disabled={showSpinner}
                        >
                            {t`I'm in a different class`}
                        </Button>
                        {errorMessage ? (
                            <>
                                <Strut size={spacing.medium_16} />
                                <LabelSmall style={{color: color.red}}>
                                    {t`We couldn't join this class: "${errorMessage}". Please try again, or use the link below to ask our Support team for help.`}
                                </LabelSmall>
                                <Spring />
                                <Button
                                    kind="tertiary"
                                    style={{width: "100%"}}
                                    href="https://khanacademy.zendesk.com/hc/requests/new"
                                    target="_blank"
                                >
                                    {t`Ask our Support team for help`}
                                </Button>
                                <Strut size={spacing.large_24} />
                            </>
                        ) : (
                            err?.code === "OVER_COACHEE_LIMIT" && (
                                <>
                                    <Strut size={spacing.medium_16} />
                                    <LabelSmall style={{color: color.red}}>
                                        {t`Your teacher has reached the limit of 1000 children and students. To add more, they must remove someone first.`}
                                    </LabelSmall>
                                    <Strut size={spacing.large_24} />
                                </>
                            )
                        )}
                    </>
                );
            }}
        </JoinStudentMutation>
    );
}
